<template>
  <div class="container-fluid mt-4 salud-resumen">
    <div class="mb-4">
      <h2 class="mb-1"><i class="bi bi-heart-pulse me-2"></i>Salud</h2>
      <p class="text-muted mb-0">Resumen de nutrición, Oura, FitDays, Coros y TrainingPeaks.</p>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted small">Cargando datos…</p>
    </div>

    <div v-else class="row g-4">
      <!-- Nutrición -->
      <div class="col-lg-6">
        <div class="card h-100 border-0 shadow-sm salud-panel">
          <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-2 py-3">
            <div class="d-flex align-items-center gap-2 min-w-0">
              <div class="salud-card-icon rounded-3 d-flex align-items-center justify-content-center salud-icon-nutricion">
                <i class="bi bi-egg-fried"></i>
              </div>
              <div class="min-w-0">
                <h5 class="mb-0">Nutrición</h5>
                <small class="text-muted">Próximos días</small>
              </div>
            </div>
            <router-link to="/nutricion" class="btn btn-sm btn-outline-primary flex-shrink-0">
              Ver todo <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="card-body">
            <template v-if="menuDias.length">
              <div class="salud-comidas-list">
                <div
                  v-for="dia in menuDias"
                  :key="dia.fecha"
                  class="salud-dia py-2 border-bottom"
                >
                  <div class="d-flex justify-content-between align-items-start gap-2">
                    <div class="min-w-0">
                      <div class="fw-semibold text-capitalize">
                        {{ dia.weekday }}
                        <span v-if="dia.fecha === menuHoy" class="badge bg-primary ms-1">Hoy</span>
                      </div>
                      <small class="text-muted">{{ formatDiaCorto(dia.fecha) }}</small>
                    </div>
                  </div>
                  <div class="salud-dia-platos small mt-2">
                    <div>
                      <span class="text-muted me-1">Comida:</span>
                      <NutricionPlatoLink :texto="dia.comida" :href="dia.comidaHref || ''" />
                    </div>
                    <div>
                      <span class="text-muted me-1">Cena:</span>
                      <NutricionPlatoLink :texto="dia.cena" :href="dia.cenaHref || ''" />
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <p v-else class="text-muted small mb-0">
              <i class="bi bi-info-circle me-1"></i>Aún no hay un menú aceptado para los próximos días.
            </p>
          </div>
        </div>
      </div>

      <!-- Oura -->
      <div class="col-lg-6">
        <div class="card h-100 border-0 shadow-sm salud-panel" :class="{ 'salud-panel--hoy': ouraEsHoyAlguno }">
          <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-2 py-3">
            <div class="d-flex align-items-center gap-2">
              <div class="salud-card-icon rounded-3 d-flex align-items-center justify-content-center salud-icon-oura">
                <i class="bi bi-activity"></i>
              </div>
              <div>
                <h5 class="mb-0">
                  Oura
                  <span v-if="ouraEsHoyAlguno" class="badge bg-primary ms-1 align-middle">Hoy</span>
                </h5>
                <small class="text-muted">Últimos scores</small>
              </div>
            </div>
            <router-link to="/salud-oura" class="btn btn-sm btn-outline-primary flex-shrink-0">
              Ver todo <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="card-body">
            <template v-if="ouraStatus?.connected && ouraLatest">
              <div class="row g-2 salud-oura-metrics">
                <div class="col-4">
                  <div
                    class="salud-metric salud-metric--readiness text-center p-2 rounded"
                    :class="{ 'salud-metric--hoy': esHoyFecha(ouraLatest.readinessDia) }"
                  >
                    <div class="salud-metric-value">{{ fmtScore(ouraLatest.readiness) }}</div>
                    <div class="salud-metric-label">Readiness</div>
                    <div v-if="ouraLatest.readinessDia" class="salud-metric-date">
                      <span v-if="esHoyFecha(ouraLatest.readinessDia)" class="badge bg-primary">Hoy</span>
                      <template v-else>{{ formatDiaEtiqueta(ouraLatest.readinessDia) }}</template>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div
                    class="salud-metric salud-metric--sleep text-center p-2 rounded"
                    :class="{ 'salud-metric--hoy': esHoyFecha(ouraLatest.sleepDia) }"
                  >
                    <div class="salud-metric-value">{{ fmtScore(ouraLatest.sleep) }}</div>
                    <div class="salud-metric-label">Sleep</div>
                    <div v-if="ouraLatest.sleepMinutes != null" class="salud-metric-extra">
                      {{ formatSleepMin(ouraLatest.sleepMinutes) }}
                    </div>
                    <div v-if="ouraLatest.sleepDia" class="salud-metric-date">
                      <span v-if="esHoyFecha(ouraLatest.sleepDia)" class="badge bg-primary">Hoy</span>
                      <template v-else>{{ formatDiaEtiqueta(ouraLatest.sleepDia) }}</template>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div
                    class="salud-metric salud-metric--activity text-center p-2 rounded"
                    :class="{ 'salud-metric--hoy': esHoyFecha(ouraLatest.activityDia) }"
                  >
                    <div class="salud-metric-value">{{ fmtScore(ouraLatest.activity) }}</div>
                    <div class="salud-metric-label">Activity</div>
                    <div v-if="ouraLatest.steps != null" class="salud-metric-extra">
                      {{ Math.round(ouraLatest.steps) }} pasos
                    </div>
                    <div v-if="ouraLatest.activityDia" class="salud-metric-date">
                      <span v-if="esHoyFecha(ouraLatest.activityDia)" class="badge bg-primary">Hoy</span>
                      <template v-else>{{ formatDiaEtiqueta(ouraLatest.activityDia) }}</template>
                    </div>
                  </div>
                </div>
              </div>
              <p class="small text-muted mt-3 mb-0">
                <template v-if="ouraEsHoyAlguno">
                  Las métricas con distintivo «Hoy» corresponden al día de hoy.
                </template>
                <template v-else>
                  Último día con dato en cada métrica (puede no ser hoy).
                </template>
              </p>
            </template>
            <p v-else-if="ouraStatus?.connected" class="text-muted small mb-0">
              Conectado. Sincroniza datos en Oura para ver Readiness, Sleep y Activity.
            </p>
            <p v-else class="text-muted small mb-0">
              <i class="bi bi-link-45deg me-1"></i>Cuenta Oura sin conectar.
            </p>
          </div>
        </div>
      </div>

      <!-- FitDays -->
      <div class="col-lg-6">
        <div class="card h-100 border-0 shadow-sm salud-panel" :class="{ 'salud-panel--hoy': fitdaysEsHoy }">
          <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-2 py-3">
            <div class="d-flex align-items-center gap-2">
              <div class="salud-card-icon rounded-3 d-flex align-items-center justify-content-center salud-icon-fitdays">
                <i class="bi bi-speedometer2"></i>
              </div>
              <div>
                <h5 class="mb-0">
                  FitDays
                  <span v-if="fitdaysEsHoy" class="badge bg-primary ms-1 align-middle">Hoy</span>
                </h5>
                <small class="text-muted">Peso</small>
              </div>
            </div>
            <router-link to="/salud-fitdays" class="btn btn-sm btn-outline-primary flex-shrink-0">
              Ver todo <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="card-body d-flex flex-column justify-content-center">
            <template v-if="fitdaysStatus?.configured && fitdaysLatest">
              <div class="text-center py-2">
                <div class="salud-peso-value">{{ fmtPeso(fitdaysLatest.peso_kg) }}</div>
                <div class="text-muted small">kg</div>
                <div v-if="fitdaysMedicionLabel" class="text-muted small mt-2">
                  <i class="bi bi-calendar3 me-1"></i>
                  <template v-if="fitdaysEsHoy">
                    Medición de <span class="badge bg-primary">Hoy</span>
                    <span class="ms-1">{{ fitdaysMedicionLabel }}</span>
                  </template>
                  <template v-else>
                    Última medición: {{ fitdaysMedicionLabel }}
                  </template>
                </div>
                <div v-if="fitdaysExtras" class="d-flex justify-content-center flex-wrap gap-3 mt-3 small text-muted">
                  <span v-if="fitdaysLatest.imc != null">IMC {{ fmtDecimal(fitdaysLatest.imc) }}</span>
                  <span v-if="fitdaysLatest.grasa_pct != null">Grasa {{ fmtDecimal(fitdaysLatest.grasa_pct) }}%</span>
                </div>
              </div>
            </template>
            <p v-else-if="fitdaysStatus?.configured" class="text-muted small mb-0 text-center">
              Configurado. Sincroniza para obtener el último peso.
            </p>
            <p v-else class="text-muted small mb-0">
              Añade credenciales FitDays en tu perfil.
            </p>
          </div>
        </div>
      </div>

      <!-- TrainingPeaks (junto a FitDays en PC; por encima de Coros) -->
      <div class="col-lg-6">
        <div class="card h-100 border-0 shadow-sm salud-panel" :class="{ 'salud-panel--hoy': tpEsHoy }">
          <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-2 py-3">
            <div class="d-flex align-items-center gap-2">
              <div class="salud-card-icon rounded-3 d-flex align-items-center justify-content-center salud-icon-tp">
                <i class="bi bi-calendar2-week"></i>
              </div>
              <div>
                <h5 class="mb-0">
                  TrainingPeaks
                  <span v-if="tpEsHoy" class="badge bg-primary ms-1 align-middle">Hoy</span>
                </h5>
                <small class="text-muted">Plan y notas del entrenador</small>
              </div>
            </div>
            <router-link to="/salud-trainingpeaks" class="btn btn-sm btn-outline-primary flex-shrink-0">
              Ver todo <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="card-body">
            <template v-if="tpResumen?.configured">
              <div v-if="tpResumen.unread" class="alert alert-primary py-2 small mb-3">
                {{ tpResumen.unread }} comentario{{ tpResumen.unread === 1 ? '' : 's' }} nuevo{{ tpResumen.unread === 1 ? '' : 's' }} del entrenador
              </div>
              <TpProximosWidget
                :configured="true"
                :entrenos="tpResumen.proximos || (tpResumen.proximo ? [tpResumen.proximo] : [])"
                :subtitulo="tpResumen.notaHoy ? (`Nota de hoy: ${tpResumen.notaHoy.title || tpResumen.notaHoy.description}`) : ''"
              />
            </template>
            <TpProximosWidget v-else :configured="false" />
          </div>
        </div>
      </div>

      <!-- Coros -->
      <div class="col-12">
        <div class="card h-100 border-0 shadow-sm salud-panel">
          <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-2 py-3">
            <div class="d-flex align-items-center gap-2">
              <div class="salud-card-icon rounded-3 d-flex align-items-center justify-content-center salud-icon-coros">
                <i class="bi bi-smartwatch"></i>
              </div>
              <div>
                <h5 class="mb-0">Deporte</h5>
                <small class="text-muted">Coros</small>
              </div>
            </div>
            <router-link to="/salud-coros" class="btn btn-sm btn-outline-primary flex-shrink-0">
              Ver todo <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="card-body">
            <template v-if="corosStatus?.configured && corosTraining">
              <CorosEstadoEntrenamiento :status="corosTraining" :hoy-entreno="corosHoy" />
            </template>
            <template v-else-if="corosStatus?.configured && proximoEntreno">
              <div class="salud-coros-next rounded p-3">
                <div class="small text-uppercase fw-semibold opacity-75 mb-1">Próximo entreno</div>
                <div class="fs-5 fw-bold text-truncate">{{ proximoEntreno.name || 'Entreno planificado' }}</div>
                <div class="d-flex flex-wrap align-items-center gap-2 mt-2 small">
                  <span class="badge bg-white bg-opacity-25">{{ fmtEntrenoDia(proximoEntreno) }}</span>
                  <span v-if="proximoEntreno.sport_name" class="badge bg-white bg-opacity-25">{{ proximoEntreno.sport_name }}</span>
                  <span v-if="proximoEntreno.estimated_time_seconds">{{ fmtDuration(proximoEntreno.estimated_time_seconds) }}</span>
                  <span v-if="proximoEntreno.exercise_count" class="opacity-75">{{ proximoEntreno.exercise_count }} pasos</span>
                </div>
              </div>
            </template>
            <template v-else-if="corosStatus?.configured">
              <p class="text-muted small mb-0">
                <i class="bi bi-calendar-x me-1"></i>No hay entrenos planificados en el calendario.
              </p>
            </template>
            <p v-else class="text-muted small mb-0">
              Configura credenciales Coros en tu perfil.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { ref, computed, onMounted } from 'vue';
import { formatMedicionAt, esHoyMadrid } from '../utils/formatSyncAt';
import NutricionPlatoLink from '@/components/NutricionPlatoLink.vue';
import TpProximosWidget from '@/components/TpProximosWidget.vue';
import CorosEstadoEntrenamiento from '@/components/CorosEstadoEntrenamiento.vue';

export default {
  name: 'Salud',
  components: { NutricionPlatoLink, TpProximosWidget, CorosEstadoEntrenamiento },
  setup() {
    const cargando = ref(true);
    const menuActivo = ref(null);
    const ouraStatus = ref(null);
    const ouraLatest = ref(null);
    const fitdaysStatus = ref(null);
    const fitdaysLatest = ref(null);
    const corosStatus = ref(null);
    const proximoEntreno = ref(null);
    const corosTraining = ref(null);
    const corosHoy = ref(null);
    const tpResumen = ref(null);

    const fitdaysMedicionAt = computed(() => (
      fitdaysStatus.value?.ultimaMedicion?.measured_at
        || fitdaysLatest.value?.measured_at
        || fitdaysLatest.value?.measured_time
        || null
    ));

    const fitdaysMedicionLabel = computed(() => {
      const at = fitdaysMedicionAt.value;
      return at ? formatMedicionAt(at, { compact: true }) : '';
    });

    const fitdaysEsHoy = computed(() => esHoyMadrid(fitdaysMedicionAt.value));

    const fitdaysExtras = computed(() => {
      const l = fitdaysLatest.value;
      return l && (l.imc != null || l.grasa_pct != null);
    });

    const menuDias = computed(() => menuActivo.value?.dias || []);
    const menuHoy = computed(() => menuActivo.value?.hoy || '');

    function esHoyFecha(iso) {
      return esHoyMadrid(iso);
    }

    const ouraEsHoyAlguno = computed(() => {
      const l = ouraLatest.value;
      if (!l) return false;
      return esHoyMadrid(l.readinessDia) || esHoyMadrid(l.sleepDia) || esHoyMadrid(l.activityDia);
    });

    const tpEsHoy = computed(() => {
      const r = tpResumen.value;
      if (!r?.configured) return false;
      if (r.notaHoy) return true;
      const lista = r.proximos || (r.proximo ? [r.proximo] : []);
      return lista.some((e) => e?.etiqueta === 'Hoy' || esHoyMadrid(e?.date));
    });

    function formatDiaCorto(fechaStr) {
      if (!fechaStr) return '';
      return new Date(`${fechaStr}T00:00:00`).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
      });
    }

    function fmtScore(v) {
      if (v == null || v === '') return '—';
      return Math.round(Number(v));
    }

    function formatSleepMin(min) {
      if (min == null) return '';
      const h = Math.floor(min / 60);
      const m = min % 60;
      return `${h} h ${m} min`;
    }

    function formatDiaEtiqueta(iso) {
      if (!iso) return '';
      const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    }

    function fmtPeso(kg) {
      if (kg == null) return '—';
      return Number(kg).toFixed(1);
    }

    function fmtDecimal(v) {
      if (v == null) return '—';
      return Number(v).toFixed(1);
    }

    function fmtDuration(sec) {
      if (sec == null) return '';
      const s = Number(sec);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h) return `${h}h ${m}m`;
      return `${m} min`;
    }

    function fmtEntrenoDia(item) {
      if (!item) return '—';
      if (item.day_label) return item.day_label;
      const s = String(item.happen_day || '');
      if (s.length === 8) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
      return s || '—';
    }

    async function cargarResumen() {
      cargando.value = true;

      const [nutRes, ouraStRes, fitStRes, corosStRes, tpRes] = await Promise.allSettled([
        axios.get('/api/nutricion/menu-activo'),
        axios.get('/api/oura/status'),
        axios.get('/api/fitdays/status'),
        axios.get('/api/coros/status'),
        axios.get('/api/trainingpeaks/resumen'),
      ]);

      if (nutRes.status === 'fulfilled') menuActivo.value = nutRes.value.data;

      if (ouraStRes.status === 'fulfilled') {
        ouraStatus.value = ouraStRes.value.data;
        if (ouraStatus.value?.connected) {
          try {
            const { data } = await axios.get('/api/oura/trends', {
              params: { days: 30, groupBy: 'day' },
            });
            ouraLatest.value = data.latest || null;
          } catch {
            ouraLatest.value = null;
          }
        }
      }

      if (fitStRes.status === 'fulfilled') {
        fitdaysStatus.value = fitStRes.value.data;
        if (fitdaysStatus.value?.configured) {
          try {
            const { data } = await axios.get('/api/fitdays/trends', {
              params: { days: 90, groupBy: 'day' },
            });
            fitdaysLatest.value = data.latest || null;
          } catch {
            fitdaysLatest.value = null;
          }
        }
      }

      if (corosStRes.status === 'fulfilled') {
        corosStatus.value = corosStRes.value.data;
        if (corosStatus.value?.configured) {
          try {
            const { data } = await axios.get('/api/coros/dashboard', { params: { days: 14 } });
            corosTraining.value = data.trainingStatus || null;
            corosHoy.value = data.hoyEntreno || null;
            if (data.hoyEntreno) proximoEntreno.value = data.hoyEntreno;
            else {
              const nw = await axios.get('/api/coros/next-workout');
              proximoEntreno.value = nw.data.workout || null;
            }
          } catch {
            corosTraining.value = null;
            try {
              const { data } = await axios.get('/api/coros/next-workout');
              proximoEntreno.value = data.workout || null;
            } catch {
              proximoEntreno.value = null;
            }
          }
        }
      }

      if (tpRes.status === 'fulfilled') {
        tpResumen.value = tpRes.value.data;
      }

      cargando.value = false;
    }

    onMounted(cargarResumen);

    return {
      cargando,
      menuDias,
      menuHoy,
      formatDiaCorto,
      ouraStatus,
      ouraLatest,
      ouraEsHoyAlguno,
      esHoyFecha,
      fitdaysStatus,
      fitdaysLatest,
      fitdaysMedicionLabel,
      fitdaysEsHoy,
      fitdaysExtras,
      corosStatus,
      proximoEntreno,
      corosTraining,
      corosHoy,
      tpResumen,
      tpEsHoy,
      fmtScore,
      formatSleepMin,
      formatDiaEtiqueta,
      fmtPeso,
      fmtDecimal,
      fmtDuration,
      fmtEntrenoDia,
    };
  },
};
</script>

<style scoped>
.salud-card-icon {
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.1rem;
}

.salud-icon-nutricion {
  background: rgba(255, 193, 7, 0.2);
  color: #b8860b;
}

.salud-icon-oura {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
}

.salud-icon-fitdays {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.salud-icon-coros {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.salud-icon-tp {
  background: rgba(111, 66, 193, 0.12);
  color: #6f42c1;
}

.salud-panel--hoy {
  box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.28), 0 0.125rem 0.5rem rgba(13, 110, 253, 0.12) !important;
}

.salud-comidas-list {
  max-height: 360px;
  overflow-y: auto;
}

.salud-dia-platos {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  word-break: break-word;
}

.salud-comida-num {
  min-width: 22px;
}

.salud-receta-link {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: rgba(13, 110, 253, 0.35);
}

.salud-receta-link:hover {
  color: #0d6efd;
}

.salud-metric {
  background: #f8f9fa;
  min-height: 5.5rem;
}

.salud-metric--hoy {
  background: rgba(13, 110, 253, 0.08);
  outline: 1px solid rgba(13, 110, 253, 0.25);
}

.salud-metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
}

.salud-metric-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
  margin-top: 0.25rem;
}

.salud-metric-date,
.salud-metric-extra {
  font-size: 0.65rem;
  color: #6c757d;
  margin-top: 0.15rem;
}

.salud-metric--readiness .salud-metric-value {
  color: #1e3a5f;
}

.salud-metric--sleep .salud-metric-value {
  color: #0d6efd;
}

.salud-metric--activity .salud-metric-value {
  color: #fd7e14;
}

.salud-peso-value {
  font-size: 2.75rem;
  font-weight: 700;
  color: #198754;
  line-height: 1;
}

.salud-coros-next {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: #fff;
}
</style>
